import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { CivilDate } from 'tc39-proposal-temporal';
import { compareCivilDate, getCivilDateToday } from '@bingads-webui-universal/temporal-utitlities';
import { DoubleMonthCalendar } from './component/double-month-calendar';
import { SpecificRange } from './component/specific-select-range';
import { SpecificCompareType } from './component/specific-compare-type';
import { RangeDisplayAndToggle } from './component/compare-toggle';
import { ComparedOptions, ComparedType } from './common/compared-options';
import { DateRangeContainer } from './component/date-range-container';
import {
  isRangeValid,
  getComparedTypeFromRange,
  getComparedRangeFromType,
  getRangeOnSelectDate,
} from './common/date-util';
import { RANGES } from './common/ranges';

const CalendarStatus = {
  Initial: 0,
  Start: 1,
  End: 2,
  CompareStart: 3,
};
const InputBoxIndex = {
  selectStart: 0,
  selectEnd: 1,
  compareStart: 2,
  compareEnd: 3,
};
const yesterday = getCivilDateToday().plus({ days: -1 });
const defaultComparedRange = { start: undefined, end: undefined };

const isDateInvalid = date => _.isNaN(_.get(date, 'year'))
  || _.isNaN(_.get(date, 'month'))
  || _.isNaN(_.get(date, 'day'));

export class ComparedDateRangeCalendar extends React.PureComponent {
  static defaultProps = {
    minDate: undefined,
    maxDate: undefined,
    onSwitchCompare: _.noop,
    selectedRange: {
      start: yesterday,
      end: yesterday,
    },
    selectedComparedRange: defaultComparedRange,
    isMonthRangePicker: false,
    isAriaLabelTranslated: false,
    selectedRangeId: undefined,
    dateRangeLogger: () => {},
  }

  constructor(props) {
    super(props);

    if (props.minDate && props.maxDate && compareCivilDate(props.minDate, props.maxDate) > 0) {
      throw new Error('minDate should be earlier than maxDate');
    }
    const selectedRangeId = props.selectedRangeId || this.findIdFromSpecificRanges(props.selectedRange) || -1;
    this.state = {
      selectedRange: props.selectedRange,
      selectedComparedRange: !props.disableCompare ? props.selectedComparedRange : {},
      calendarStatus: CalendarStatus.Initial,
      isCompareEnabled: !props.disableCompare,
      isCompareOn: !props.disableCompare && isRangeValid(props.selectedComparedRange),
      comparedOption: getComparedTypeFromRange({
        range: props.selectedRange,
        comparedRange: props.selectedComparedRange,
        selectedRangeId,
      }),
      selectedRangeId,
    };
  }

  onClickSpecificRange = (rangeId) => {
    const { comparedOption } = this.state;
    let { isCompareOn } = this.state;
    const { range } = _.find(this.props.specificRanges, r => r.rangeId === rangeId);
    const selectedRange = isRangeValid(range) ? range : this.state.selectedRange;
    if (rangeId === RANGES.EntireTime) {
      this.onSwitchCompare(false);
      isCompareOn = false;
    }
    this.setState({
      selectedRange,
      selectedRangeId: rangeId,
    });

    if (!isCompareOn && isRangeValid(range)) {
      this.props.onPickDateRanges({
        selectedRange,
        selectedComparedRange: defaultComparedRange,
        comparedOption,
        rangeId,
      });
      return;
    }

    this.setState({
      calendarStatus: CalendarStatus.Initial,
    });

    if (isCompareOn && comparedOption.type !== ComparedType.Custom) {
      this.setState({
        selectedComparedRange: getComparedRangeFromType({
          range: selectedRange,
          type: comparedOption.type,
          selectedRangeId: rangeId,
        }),
      });
    }
  }

  onSelectDate = (date) => {
    const {
      isCompareOn,
      calendarStatus,
      selectedRange,
      selectedComparedRange,
    } = this.state;

    if (calendarStatus === CalendarStatus.Initial) {
      this.setStateForRangeChange(getRangeOnSelectDate({
        start: date,
        end: selectedRange.end > date ? selectedRange.end : null,
        isMonthRangePicker: this.props.isMonthRangePicker,
        minDate: this.props.minDate,
        maxDate: this.props.maxDate,
      }));
    }
    if (calendarStatus === CalendarStatus.Start) {
      this.setStateForRangeChange(getRangeOnSelectDate({
        start: selectedRange.start,
        end: date,
        isMonthRangePicker: this.props.isMonthRangePicker,
        minDate: this.props.minDate,
        maxDate: this.props.maxDate,
      }));
    }
    if (calendarStatus === CalendarStatus.End && isCompareOn) {
      this.setState({
        selectedComparedRange: getRangeOnSelectDate({
          start: date,
          end: selectedComparedRange.end > date ? selectedComparedRange.end : null,
        }),
      });
    }
    if (calendarStatus === CalendarStatus.CompareStart && isCompareOn) {
      this.setState({
        selectedComparedRange: getRangeOnSelectDate({
          start: selectedComparedRange.start,
          end: date,
        }),
      });
    }

    this.setState(state => ({
      calendarStatus: this.getNextStatus(state),
    }));
  }

  onSwitchCompare = (isChecked) => {
    const { selectedRangeId } = this.state;

    if (isChecked) {
      this.setState({
        selectedComparedRange: getComparedRangeFromType({
          range: this.state.selectedRange,
          type: ComparedType.LastPeriod,
          selectedRangeId,
        }),
        comparedOption:
          _.find(ComparedOptions, option => option.type === ComparedType.LastPeriod),
        isCompareOn: true,
      });
    } else {
      this.setState({
        selectedComparedRange: defaultComparedRange,
        isCompareOn: false,
      });
    }
    this.props.onSwitchCompare({ isChecked });
  }

  onChangeComparedType = (typeId) => {
    const { selectedRangeId } = this.state;
    const selectedOption = _.find(ComparedOptions, option => option.type === typeId);
    this.setState({
      comparedOption: selectedOption,
    });

    if (selectedOption.type === ComparedType.Custom) {
      this.setState({
        calendarStatus: CalendarStatus.End,
      });
    } else {
      this.setState({
        calendarStatus: CalendarStatus.Initial,
        selectedComparedRange: getComparedRangeFromType({
          range: this.state.selectedRange,
          type: selectedOption.type,
          selectedRangeId,
        }),
      });
    }
  }

  onRangeInputChange = index => (date) => {
    const { minDate, maxDate } = this.props;
    let civilDate = date;

    if (minDate && compareCivilDate(civilDate, minDate) < 0) {
      civilDate = minDate;
    } else if (maxDate && compareCivilDate(civilDate, maxDate) > 0) {
      civilDate = maxDate;
    }

    const { selectedRange, selectedComparedRange } = this.state;

    if (index === 0 && compareCivilDate(selectedRange.start, civilDate) !== 0) {
      const range = {
        start: civilDate,
        end: compareCivilDate(selectedRange.end, civilDate) > 0 ? selectedRange.end : civilDate,
      };
      this.setStateForRangeChange(range);
      this.setState({
        calendarStatus: CalendarStatus.Start,
      });
    } else if (index === 1 && compareCivilDate(selectedRange.end, civilDate) !== 0) {
      const start1 = compareCivilDate(selectedRange.start, civilDate) < 0 ?
        selectedRange.start : civilDate;
      const range = {
        start: start1,
        end: civilDate,
      };
      this.setStateForRangeChange(range);
      this.setState(state => ({
        calendarStatus: (state.isCompareOn &&
          state.comparedOption && state.comparedOption.type === ComparedType.Custom) ?
          CalendarStatus.End :
          CalendarStatus.Initial,
      }));
    } else if (index === 2 &&
      compareCivilDate(selectedComparedRange.start, civilDate) !== 0) {
      this.setState(state => ({
        selectedComparedRange: {
          start: civilDate,
          end: compareCivilDate(state.selectedComparedRange.end, civilDate) > 0 ?
            state.selectedComparedRange.end :
            civilDate,
        },
        comparedOption:
          _.find(ComparedOptions, option => option.type === ComparedType.Custom),
        calendarStatus: CalendarStatus.CompareStart,
      }));
    } else if (index === 3 &&
      compareCivilDate(selectedComparedRange.end, civilDate) !== 0) {
      this.setState(state => ({
        selectedComparedRange: {
          start: compareCivilDate(state.selectedComparedRange.start, civilDate) < 0 ?
            state.selectedComparedRange.start :
            civilDate,
          end: civilDate,
        },
        comparedOption:
          _.find(ComparedOptions, option => option.type === ComparedType.Custom),
        calendarStatus: CalendarStatus.Initial,
      }));
    }
  }

  componentDidMount() {
    // log the impression on mount
    if (this.props.dateRangeLogger) {
      this.props.dateRangeLogger({
        method: 'comparedDateRangePickerImpression',
      });
    }
  }

  onInputFocus = (index) => {
    this.setState({
      calendarStatus: index,
    });
  }

  setStateForRangeChange(range) {
    if (isDateInvalid(range.start) || isDateInvalid(range.end)) {
      return;
    }
    this.setState({
      selectedRange: range,
    });
    const selectedRangeId = this.findIdFromSpecificRanges(range);
    this.setState({
      selectedRangeId,
    });

    const { isCompareOn, comparedOption } = this.state;
    if (isCompareOn && comparedOption && comparedOption.type !== ComparedType.Custom) {
      this.setState({
        selectedComparedRange: getComparedRangeFromType({
          range,
          type: comparedOption.type,
          selectedRangeId,
        }),
      });
    }
  }

  getNextStatus(state) {
    if ((!state.isCompareOn && state.calendarStatus === CalendarStatus.Start) ||
      (state.isCompareOn && state.calendarStatus === CalendarStatus.CompareStart) ||
      (state.isCompareOn &&
        state.calendarStatus === CalendarStatus.Start &&
        state.comparedOption && state.comparedOption.type !== ComparedType.Custom)) {
      return CalendarStatus.Initial;
    }

    return state.calendarStatus + 1;
  }

  findIdFromSpecificRanges = (selectedRange) => {
    const specificRange = _.find(
      this.props.specificRanges,
      r =>
        r.range.start && r.range.end &&
        selectedRange.start &&
        selectedRange.end &&
        compareCivilDate(r.range.start, selectedRange.start) === 0 &&
        compareCivilDate(r.range.end, selectedRange.end) === 0
    );
    return specificRange ? specificRange.rangeId : _.find(this.props.specificRanges, r => r.key === 'Custom').rangeId;
  }

  render() {
    const {
      selectedRange,
      selectedComparedRange,
      isCompareEnabled,
      isCompareOn,
      comparedOption,
      selectedRangeId,
      calendarStatus,
    } = this.state;

    const withCompareClassName = isCompareOn ? 'compare' : 'compare-off';
    const leftPartClassName = classNames('left-part', withCompareClassName);

    return (
      <div className="compared-date-range-calendar">
        <div className={leftPartClassName}>
          <div className="range-primary">
            <DateRangeContainer
              dateRange={selectedRange}
              onStartInputChange={this.onRangeInputChange(InputBoxIndex.selectStart)}
              onEndInputChange={this.onRangeInputChange(InputBoxIndex.selectEnd)}
              i18n={this.props.i18n}
              minDate={this.props.minDate}
              maxDate={this.props.maxDate}
              timeZone={this.props.timeZone}
              dateFormat={this.props.dateFormat}
              highlightInputIndex={calendarStatus > 1 ? -1 : calendarStatus}
              onInputFocus={i => this.onInputFocus(i)}
              startDateAriaLabel={this.props.i18n.getString(_TL_('Custom start date'))}
              endDateAriaLabel={this.props.i18n.getString(_TL_('Custom end date'))}
              isMonthRangePicker={this.props.isMonthRangePicker}
            />
            <SpecificRange
              i18n={this.props.i18n}
              onClickSpecificRange={this.onClickSpecificRange}
              selectedRangeId={selectedRangeId}
              specificRanges={this.props.specificRanges}
            />
          </div>
          {isCompareOn && ((
            <div className="range-compare">
              <DateRangeContainer
                onChangeSelectOption={this.onChangeComparedType}
                selectedOption={comparedOption}
                dateRange={selectedComparedRange}
                onStartInputChange={this.onRangeInputChange(InputBoxIndex.compareStart)}
                onEndInputChange={this.onRangeInputChange(InputBoxIndex.compareEnd)}
                i18n={this.props.i18n}
                minDate={this.props.minDate}
                maxDate={this.props.maxDate}
                timeZone={this.props.timeZone}
                dateFormat={this.props.dateFormat}
                highlightInputIndex={calendarStatus < 2 ? -1 : calendarStatus % 2}
                onInputFocus={i => this.onInputFocus(i + 2)}
                startDateAriaLabel={this.props.i18n.getString(_TL_('Custom compared start date'))}
                endDateAriaLabel={this.props.i18n.getString(_TL_('Custom compared end date'))}
              />
              <SpecificCompareType
                onChangeSelectOption={this.onChangeComparedType}
                selectedOption={comparedOption}
                i18n={this.props.i18n}
              />
            </div>))}
        </div>
        <div className="divider" />
        <div className="customize-range-select">
          <RangeDisplayAndToggle
            i18n={this.props.i18n}
            selectedDateRange={selectedRange}
            selectedComparedDateRange={selectedComparedRange}
            specificRangeId={selectedRangeId}
            specificRanges={this.props.specificRanges}
            onRangeInputChange={this.onRangeInputChange}
            onChangeComparedType={this.onChangeComparedType}
            onSwitchCompare={this.onSwitchCompare}
            isCompareEnabled={isCompareEnabled}
            isCompareOn={isCompareOn}
            selectedOption={comparedOption}
            highlightInputIndex={calendarStatus}
            minDate={this.props.minDate}
            maxDate={this.props.maxDate}
            onInputFocus={this.onInputFocus}
            timeZone={this.props.timeZone}
          />
          <div className="calendar">
            <DoubleMonthCalendar
              selectedRanges={_.compact([
                selectedRange,
                isRangeValid(selectedComparedRange) ? selectedComparedRange : null,
              ])}
              onSelectDateCallback={this.onSelectDate}
              minDate={this.props.minDate}
              maxDate={this.props.maxDate}
              today={this.props.today}
              i18n={this.props.i18n}
              isMonthRangePicker={this.props.isMonthRangePicker}
              isAriaLabelTranslated={this.props.isAriaLabelTranslated}
            />
          </div>
          <div className="apply-and-close">
            {this.props.renderApply({
              selectedRange,
              selectedComparedRange: isCompareOn && selectedRangeId !== RANGES.EntireTime
                ? selectedComparedRange : defaultComparedRange,
              comparedOption,
              rangeId: selectedRangeId,
            })}
            {this.props.renderDismiss()}
          </div>
        </div>
      </div>
    );
  }
}

ComparedDateRangeCalendar.propTypes = {
  dateRangeLogger: PropTypes.func,
  minDate: PropTypes.instanceOf(CivilDate),
  maxDate: PropTypes.instanceOf(CivilDate),
  today: PropTypes.instanceOf(CivilDate).isRequired,
  selectedRange: PropTypes.shape({
    start: PropTypes.instanceOf(CivilDate),
    end: PropTypes.instanceOf(CivilDate),
  }),
  timeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  selectedComparedRange: PropTypes.shape({
    start: PropTypes.instanceOf(CivilDate),
    end: PropTypes.instanceOf(CivilDate),
  }),
  specificRanges: PropTypes.arrayOf(PropTypes.shape({
    rangeId: PropTypes.number,
    key: PropTypes.string,
    range: PropTypes.shape({
      start: PropTypes.instanceOf(CivilDate),
      end: PropTypes.instanceOf(CivilDate),
    }),
  })).isRequired,
  onPickDateRanges: PropTypes.func.isRequired,
  onSwitchCompare: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseCivilDate: PropTypes.func.isRequired,
  }).isRequired,
  disableCompare: PropTypes.bool.isRequired,
  renderApply: PropTypes.func.isRequired,
  renderDismiss: PropTypes.func.isRequired,
  isMonthRangePicker: PropTypes.bool,
  isAriaLabelTranslated: PropTypes.bool,
  selectedRangeId: PropTypes.number,
};
